import React, { useCallback, useEffect, useRef, useState } from "react";
import { Drawer, IconButton, CircularProgress, Tooltip, Tabs, Tab } from "@material-ui/core";
import { GridCloseIcon } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Typography } from "@mui/material";
import { useAnalyticsOverview } from "../../context/AnalyticsOverviewContext";
import { quantityOverviewService, computeOverview as computeQuantityOverview } from "../QuantityOverview/quantityOverviewService";
import { bagsOverviewService, computeOverview as computeBagsOverview } from "../BagsOverview/bagsOverviewService";
import QuantityTable from "./QuantityTable";
import BagsTable from "./BagsTable";
import { primary, darkBlue } from "../../styles/colors";

const CACHE_TTL_MS = 5 * 60 * 1000;

const StatChip = ({ label, value, highlight }) => (
  <div
    style={{
      padding: "10px 16px",
      borderRadius: "8px",
      backgroundColor: highlight ? "rgba(241, 154, 48, 0.1)" : "#f8f9fa",
      border: highlight ? `1px solid ${primary}` : "1px solid #e9ecef",
      minWidth: "140px",
    }}
  >
    <div style={{ fontSize: "0.7rem", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" }}>
      {label}
    </div>
    <div style={{ fontSize: "1.1rem", fontWeight: 700, color: highlight ? primary : darkBlue }}>
      {value === null || value === undefined ? "—" : value.toLocaleString("en-IN")}
    </div>
  </div>
);

const emptyDataset = {
  rows: [],
  next: null,
  count: 0,
  summary: null,
  loading: false,
  loadingMore: false,
  error: null,
  lastFetched: null,
};

const AnalyticsOverview = () => {
  const { state, dispatch } = useAnalyticsOverview();
  const { open, activeTab } = state;

  const [quantity, setQuantity] = useState(emptyDataset);
  const [bags, setBags] = useState(emptyDataset);
  const quantityRef = useRef(quantity);
  const bagsRef = useRef(bags);
  quantityRef.current = quantity;
  bagsRef.current = bags;

  const fetchQuantity = useCallback(async () => {
    setQuantity((prev) => ({ ...prev, loading: true, error: null }));
    const res = await quantityOverviewService.getPage();
    if (res.error) {
      setQuantity((prev) => ({ ...prev, loading: false, error: "Error fetching quantity data" }));
    } else {
      setQuantity({
        rows: res.results,
        next: res.next,
        count: res.count,
        summary: computeQuantityOverview(res.results),
        loading: false,
        loadingMore: false,
        error: null,
        lastFetched: Date.now(),
      });
    }
  }, []);

  const loadMoreQuantity = useCallback(async () => {
    const current = quantityRef.current;
    if (!current.next || current.loadingMore) return;
    setQuantity((prev) => ({ ...prev, loadingMore: true }));
    const res = await quantityOverviewService.getPage(current.next);
    if (res.error) {
      setQuantity((prev) => ({ ...prev, loadingMore: false, error: "Error fetching quantity data" }));
    } else {
      setQuantity((prev) => {
        const rows = prev.rows.concat(res.results);
        return { ...prev, rows, next: res.next, summary: computeQuantityOverview(rows), loadingMore: false };
      });
    }
  }, []);

  const fetchBags = useCallback(async () => {
    setBags((prev) => ({ ...prev, loading: true, error: null }));
    const res = await bagsOverviewService.getPage();
    if (res.error) {
      setBags((prev) => ({ ...prev, loading: false, error: "Error fetching bags data" }));
    } else {
      setBags({
        rows: res.results,
        next: res.next,
        count: res.count,
        summary: computeBagsOverview(res.results, res.summary),
        loading: false,
        loadingMore: false,
        error: null,
        lastFetched: Date.now(),
      });
    }
  }, []);

  const loadMoreBags = useCallback(async () => {
    const current = bagsRef.current;
    if (!current.next || current.loadingMore) return;
    setBags((prev) => ({ ...prev, loadingMore: true }));
    const res = await bagsOverviewService.getPage(current.next);
    if (res.error) {
      setBags((prev) => ({ ...prev, loadingMore: false, error: "Error fetching bags data" }));
    } else {
      setBags((prev) => {
        const rows = prev.rows.concat(res.results);
        return { ...prev, rows, next: res.next, summary: computeBagsOverview(rows, res.summary), loadingMore: false };
      });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (activeTab === 0) {
      const isStale = !quantityRef.current.lastFetched || Date.now() - quantityRef.current.lastFetched > CACHE_TTL_MS;
      if (isStale && !quantityRef.current.loading) fetchQuantity();
    } else {
      const isStale = !bagsRef.current.lastFetched || Date.now() - bagsRef.current.lastFetched > CACHE_TTL_MS;
      if (isStale && !bagsRef.current.loading) fetchBags();
    }
  }, [open, activeTab, fetchQuantity, fetchBags]);

  const handleClose = () => dispatch({ type: "CLOSE_MODAL" });
  const handleTabChange = (event, newValue) => dispatch({ type: "SET_TAB", payload: newValue });
  const handleRefresh = () => (activeTab === 0 ? fetchQuantity() : fetchBags());

  const dataset = activeTab === 0 ? quantity : bags;

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <div
        style={{
          width: "min(1400px, 92vw)",
          height: "100%",
          padding: "20px 24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <Typography style={{ fontSize: "1.2rem", fontWeight: 600, color: darkBlue }}>
            Analytics
          </Typography>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={handleRefresh} disabled={dataset.loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={handleClose}>
              <GridCloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          TabIndicatorProps={{ style: { backgroundColor: primary, height: "3px" } }}
          style={{ borderBottom: "1px solid #e9ecef", marginBottom: "16px" }}
        >
          <Tab label="Quantity" style={{ textTransform: "none", fontWeight: 500 }} />
          <Tab label="Bags" style={{ textTransform: "none", fontWeight: 500 }} />
        </Tabs>

        {dataset.loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
            <CircularProgress style={{ color: primary }} />
          </div>
        ) : dataset.error ? (
          <Typography style={{ color: "#dc3545", fontSize: "0.85rem" }}>{dataset.error}</Typography>
        ) : (
          <>
            <div style={{ display: "flex", gap: "12px", marginBottom: "8px", flexWrap: "wrap" }}>
              {activeTab === 0 ? (
                <>
                  <StatChip label="Sales Total" value={quantity.summary?.salesTotal} />
                  <StatChip label="PO Created" value={quantity.summary?.poCreated} />
                  <StatChip label="Cargo Received" value={quantity.summary?.cargoReceived} />
                  <StatChip label="Balance to Purchase" value={quantity.summary?.balanceToPurchase} highlight />
                </>
              ) : (
                <>
                  <StatChip label="Bags Ordered" value={bags.summary?.bagsOrdered} />
                  <StatChip label="Bags Dispatched" value={bags.summary?.bagsDispatched} />
                  <StatChip label="Balance Bags" value={bags.summary?.balanceBags} highlight />
                  <StatChip label="Total Used Bags" value={bags.summary?.totalUsedBags} />
                </>
              )}
            </div>

            <Typography style={{ fontSize: "0.7rem", color: "#adb5bd", marginBottom: "8px" }}>
              {`Showing ${dataset.rows.length} of ${dataset.count}${dataset.next ? " · scroll to load more" : ""}`}
            </Typography>

            {activeTab === 0 ? (
              <QuantityTable
                rows={quantity.rows}
                hasMore={Boolean(quantity.next)}
                loadingMore={quantity.loadingMore}
                onLoadMore={loadMoreQuantity}
              />
            ) : (
              <BagsTable
                rows={bags.rows}
                hasMore={Boolean(bags.next)}
                loadingMore={bags.loadingMore}
                onLoadMore={loadMoreBags}
              />
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default AnalyticsOverview;
